import React from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { FaFilePdf } from 'react-icons/fa';

/**
 * Component for exporting implementation roadmap data to PDF
 * @param {Object} props - Component props
 * @param {Array} props.recommendations - Array of recommendation objects to export
 * @param {string} props.title - Title for the PDF report
 * @param {string} props.organizationName - Name of the organization
 */
const RoadmapPdfExport = ({ recommendations, title, organizationName }) => {
  // Generate the current date in a readable format
  const generateDateString = () => {
    const date = new Date();
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Generate and download the PDF
  const generatePDF = () => {
    // Create PDF document - use A4 landscape for better table fit
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    
    // Set document properties
    pdf.setProperties({
      title: 'Implementation Roadmap',
      subject: 'Business Resilience Navigator - Implementation Roadmap',
      author: organizationName,
      creator: 'Business Resilience Navigator'
    });
    
    // Add header
    pdf.setFontSize(14);
    pdf.setTextColor(36, 52, 68); // #243444
    pdf.text('Business Resilience Navigator - Implementation Roadmap', 14, 15);
    
    // Add organization name and date
    const currentDate = generateDateString();
    pdf.setFontSize(10);
    pdf.text(`Generated: ${currentDate}`, pdf.internal.pageSize.getWidth() - 14, 15, { align: 'right' });
    pdf.text(`Generated by: ${organizationName || 'Your Organization'}`, pdf.internal.pageSize.getWidth() - 14, 20, { align: 'right' });
    
    // Collect all recommendations into a single array for the table
    const tableData = [];
    
    // Process all recommendations for the first page table only
    recommendations.forEach(category => {
      if (category.recommendations && category.recommendations.length > 0) {
        category.recommendations.forEach(rec => {
          tableData.push([
            rec.text || '',
            rec.priority || 'Medium',
            rec.status || 'Not Started',
            rec.dueDate ? new Date(rec.dueDate).toLocaleDateString() : 'Not set',
            rec.assignedTo || 'Unassigned'
          ]);
        });
      }
    });
    
    // Define table headers
    const headers = [
      'Recommendation',
      'Priority',
      'Status',
      'Due Date',
      'Assigned To'
    ];
    
    // Generate table - only include the first page worth of data
    pdf.autoTable({
      head: [headers],
      body: tableData,
      startY: 25,
      theme: 'grid',
      headStyles: {
        fillColor: [36, 52, 68], // #243444
        textColor: [255, 250, 250], // #FFFAFA
        fontStyle: 'bold',
        halign: 'left'
      },
      bodyStyles: {
        textColor: [76, 84, 84], // #4C5454
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      columnStyles: {
        0: { cellWidth: 'auto' }, // Recommendation
        1: { cellWidth: 20 },     // Priority
        2: { cellWidth: 20 },     // Status
        3: { cellWidth: 25 },     // Due Date
        4: { cellWidth: 30 }      // Assigned To
      },
      // Limit to a single page
      didDrawPage: function(data) {
        // Force only one page by returning false after the first page
        return data.pageCount === 1;
      }
    });
    
    // Add footer
    pdf.setFontSize(8);
    pdf.setTextColor(108, 117, 125); // #6c757d
    pdf.text('Business Resilience Navigator', pdf.internal.pageSize.getWidth() / 2, pdf.internal.pageSize.getHeight() - 10, { align: 'center' });
    
    // Save the PDF
    pdf.save(`Implementation_Roadmap_${currentDate.replace(/,/g, '').replace(/ /g, '_')}.pdf`);
  };

  return (
    <button 
      onClick={generatePDF} 
      style={{
        backgroundColor: '#243444',
        color: '#FFFAFA',
        border: 'none',
        borderRadius: '4px',
        padding: '8px 16px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        fontWeight: 'bold'
      }}
    >
      <FaFilePdf /> Export as PDF
    </button>
  );
};

export default RoadmapPdfExport;
